import React, { useEffect, useState } from "react";
import { X, Loader2, Users, HelpCircle } from "lucide-react";
import ContactLink from "../../contacts/components/ContactLink";
import { fetchProspectBuyers } from "../buyerMatch";
import { MatchLevelChip, NextAction, ReasonChips, scoreTitle } from "../../analytics/components/MatchLevel";

/**
 * The buyers behind a card's "N buyers" chip on Prospects (DOMA 13374913603),
 * ranked the way Find Buyers ranks them.
 */
const ProspectBuyersModal = ({ item, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchProspectBuyers(item);
        if (!cancelled) setData(res);
      } catch (error) {
        console.error("Failed to load matching buyers:", error);
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [item]);

  const buyers = data?.buyers || [];
  // Buyers who named only a capital city ("Melbourne") come last, in their own
  // section: interested in the city, not this area (DOMA 13374913603).
  const named = buyers.filter((b) => !b.broad_location);
  const broad = buyers.filter((b) => b.broad_location);

  const card = (b) => (
    <div key={`${b.source}-${b.ref_id}`} className="rounded-xl border border-gray-100 bg-gray-50/60 p-3">
      <div className="flex items-baseline justify-between gap-3">
        <ContactLink
          identity={{ contactId: b.contact_id, buyerId: b.buyer_id, email: b.email, name: b.name, phone: b.phone }}
          returnTo="/prospects"
          returnLabel="Prospects"
          className="font-semibold text-gray-900 hover:text-[#3a99a3] hover:underline text-left"
        >
          {b.name || b.email || "Buyer"}
        </ContactLink>
        <span className="inline-flex items-baseline gap-2 shrink-0">
          {b.match_score != null && (
            <span className="text-sm font-bold text-[#0e7490]" title={scoreTitle(b.score_parts)}>{b.match_score} match</span>
          )}
          <MatchLevelChip match={b} />
        </span>
      </div>
      <div className="mt-0.5 text-xs text-gray-500 flex flex-wrap gap-x-3">
        {b.phone && <a href={`tel:${b.phone}`} className="hover:underline">{b.phone}</a>}
        {b.email && <span>{b.email}</span>}
      </div>
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        <ReasonChips match={b} />
      </div>
      <NextAction match={b} className="mt-1.5" />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onMouseDown={onClose}>
      <div
        role="dialog"
        aria-label={`Matching buyers for ${item.title}`}
        className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[88vh] flex flex-col"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-gray-100">
          <div className="min-w-0">
            <h3 className="text-base font-bold text-[#2D3441] flex items-center gap-2">
              <Users className="w-4 h-4 text-[#3a99a3]" /> Matching buyers
            </h3>
            <p className="text-xs text-gray-500 break-words">{item.fullAddress || item.subtitle || item.title}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-2">
          {data?.details_unknown && (
            <p className="flex items-center gap-1.5 rounded-lg bg-amber-50 text-amber-700 text-xs px-3 py-2">
              <HelpCircle className="w-3.5 h-3.5 shrink-0" />
              No property details on file for this address, so these buyers matched on suburb only.
            </p>
          )}
          {loading ? (
            <div className="flex justify-center py-10 text-gray-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : failed ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              We could not load the matching buyers just now. Close this and try again.
            </div>
          ) : buyers.length === 0 ? (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
              No buyer in the CRM fits this property yet.
            </div>
          ) : (
            <>
              {named.map(card)}
              {broad.length > 0 && (
                <>
                  <div className="pt-2">
                    <h4 className="text-sm font-semibold text-gray-700">
                      Broad interest <span className="font-normal text-gray-400">({broad.length})</span>
                    </h4>
                    <p className="text-xs text-gray-500">
                      Named only a capital city, not this area. Matched on budget and property type.
                    </p>
                  </div>
                  {broad.map(card)}
                </>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
          <span className="text-sm text-gray-500">
            {loading || failed ? "" : `${buyers.length} ${buyers.length === 1 ? "buyer" : "buyers"}`}
          </span>
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-xl bg-gray-900 text-white text-sm font-medium hover:bg-gray-800">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProspectBuyersModal;
